import WebRendererSetup from '@/components/Fitstation3DScan/WebRendererSetup';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { AmbientLight, BackSide, Box3, BoxGeometry, DirectionalLight, DoubleSide, Group, Mesh, ShaderMaterial, ShadowMaterial, TOUCH, Vector3 } from 'three';

export default class ThreeDFootSetup extends WebRendererSetup {
	private ambientLight?: AmbientLight;
	private counter: number = 0;
	private outlineShaderMaterial?: ShaderMaterial;
	private shoeShaderMaterial?: ShaderMaterial;
	private shoeShaderUniforms: any;
	private outlineShaderUniforms: any;
	private light: any;
	private details: any;

	getControls(): OrbitControls {
		const controls = new OrbitControls(this.webRenderer.camera, this.webRenderer.renderer.domElement);
		controls.minDistance = 2.9;
		controls.maxDistance = 2.9;
		controls.enableDamping = true;
		controls.dampingFactor = 0.45;
		controls.enableZoom = true;
		controls.rotateSpeed = 0.1;
		controls.minPolarAngle = 1.0;
		controls.enableKeys = false;
		controls.maxPolarAngle = 3.0;
		// controls.autoRotate = true;
		controls.target = new Vector3(0.0, 0, 0);
		// @ts-ignore
		controls.mouseButtons['RIGHT'] = 0;
		// @ts-ignore
		controls.mouseButtons['MIDDLE'] = 0;
		controls.keys['BOTTOM'] = 0;
		controls.keys['LEFT'] = 0;
		controls.keys['RIGHT'] = 0;
		controls.keys['UP'] = 0;
		// @ts-ignore
		controls.noPan = true;
		controls.enablePan = false;
		controls.zoomSpeed = 1.0;
		controls.update();
		return controls;
	}

	onObjectsLoaded(index: number, group) {}

	render(urls: string[], isObj: boolean, canvasContainer: HTMLElement, details?: any) {
		this.details = details;
		this.webRenderer.renderer.domElement.addEventListener(
			'mousedown',
			() => {
				if (!this.webRenderer.controls) {
					return;
				}
				this.webRenderer.controls.autoRotate = false;
			},
			false
		);

		this.webRenderer.renderer.domElement.addEventListener(
			'mouseup',
			() => {
				setTimeout(() => {
					if (!this.webRenderer.controls) {
						return;
					}
					this.webRenderer.controls.autoRotate = true;
				}, 2000);
			},
			false
		);

		this.webRenderer.renderer.domElement.addEventListener('touchstart', () => {
			if (!this.webRenderer.controls) {
				return;
			}
			this.webRenderer.controls.autoRotate = false;
		});

		this.webRenderer.renderer.domElement.addEventListener('touchend', () => {
			setTimeout(() => {
				if (!this.webRenderer.controls) {
					return;
				}
				this.webRenderer.controls.autoRotate = true;
			}, 2000);
		});

		this.shoeShaderUniforms = {
			linesMode: { value: true },
			colorOutline: { value: new Vector3(0.9, 0.9, 0.9) },
			colorIn: { value: new Vector3(0.8, 0.87, 0.93) },
			colorOut: { value: new Vector3(0.5, 0.66, 0.83) },
			lineWidth: { value: 1 },
			lineDensity: { value: new Vector3(8.0, 16.0, 4.0) },
			lineDensityGrid: { value: new Vector3(8.0, 8.0, 8.0) },
			timeScale: { value: new Vector3(-0.1, 0.1, 0.1) },
			opacity: { value: 0.0 },
			colorLines: { value: new Vector3(0.01, 0.1, 0.15) },
			freqViewMask: { value: 0.2 },
			timeViewMask: { value: -0.2 },
			widthViewMask: { value: 5.0 },
			time: { value: 0.0 }
		};

		this.outlineShaderUniforms = {
			linesMode: { value: true },
			colorOutline: { value: new Vector3(0.408, 0.537, 0.651) },
			vertexOffset: { value: 0.01 },
			opacity: { value: 0.0 }
		};

		this.shoeShaderMaterial = new ShaderMaterial({
			side: DoubleSide,
			uniforms: this.shoeShaderUniforms,
			vertexShader: this.shaderData.shader_scanFeet_Vert(),
			fragmentShader: this.shaderData.shader_ScanFeet_Frag()
		});

		this.outlineShaderMaterial = new ShaderMaterial({
			side: BackSide,
			uniforms: this.outlineShaderUniforms,
			vertexShader: this.shaderData.shader_scanOutline_Vert(),
			fragmentShader: this.shaderData.shader_scanOutline_Frag()
		});

		this.shoeShaderMaterial.extensions['derivatives'] = true;
		this.outlineShaderMaterial.extensions['derivatives'] = true;

		const planeGeometry = new BoxGeometry(0.75 * 25, 0.05, 0.75 * 25, 10, 10);
		const shadowMaterial = new ShadowMaterial();
		shadowMaterial.opacity = 0.1;
		const plane = new Mesh(planeGeometry, shadowMaterial);
		plane.receiveShadow = true;
		plane.position.add(new Vector3(0, -0.229, 0));
		if (!this.webRenderer || !canvasContainer) {
			return;
		}
		this.webRenderer.bufferScene.add(plane);

		this.webRenderer.OnUpdate = () => {
			if (!this.ambientLight) {
				return;
			}

			this.light.castShadow = this.webRenderer.camera.position.y > -0.05 ? true : false;

			if (this.ambientLight.intensity > 0.75) {
				this.ambientLight.intensity -= 0.02;
			}

			this.counter += 0.02;
			this.shoeShaderUniforms.time.value = this.counter;
			this.shoeShaderUniforms.opacity.value = this.counter;
			this.outlineShaderUniforms.opacity.value = this.counter;
		};

		this.webRenderer.FitToElement(canvasContainer);
		this.ambientLight = new AmbientLight(0xe0e0e0, 2); // soft white light
		this.webRenderer.bufferScene.add(this.ambientLight);

		const light = new DirectionalLight(0xffffff, 1.0);
		this.light = light;
		light.position.set(1, 5, 0);
		light.castShadow = true; // default false
		light.shadow.mapSize.width = 512;
		light.shadow.mapSize.height = 512;
		light.shadow.camera.left = -0.5;
		light.shadow.camera.right = 0.5;
		light.shadow.camera.top = 0.5;
		light.shadow.camera.bottom = -0.5;
		light.shadow.camera.near = 1.0;
		light.shadow.camera.far = 6;
		light.shadow.radius = 6;
		this.webRenderer.bufferScene.add(light);

		// https://groups.csail.mit.edu/graphics/classes/6.837/F03/models/teapot.obj
		this.webRenderer.RenderObjs(urls, isObj, async (obj, i) => {
			if (details?.origin.includes('SDK')) {
				let bbox = new Box3().setFromObject(obj);
				obj.position.add(new Vector3(bbox.min.x, bbox.min.y, bbox.min.z));
			}

			obj.rotateZ(90 * 0.01745329252);
			obj.rotateY(90 * 0.01745329252);

			if (details?.meta?.scanMode === 'FOAMBOX') {
				obj.position.add(new Vector3(0.2, 0, -0.1)); // foambox
			}

			if (i == 0) {
				obj.position.add(new Vector3(0.2, -0.2, 0.5));
				if (details?.origin.includes('SDK')) {
					obj.position.set(0, obj.position.y, 0);
					obj.scale.set(obj.scale.x * 0.9, obj.scale.x * 0.9, obj.scale.x * 0.9);
				}
				obj.children[0].castShadow = true;
				const outlineClone = obj.clone();
				await this.SetOutlineShoeMaterial(outlineClone);
				if (!this.webRenderer) {
					return;
				}
				this.webRenderer.bufferScene.add(outlineClone);
			}

			await this.SetShoeMaterial(obj);
		});

		this.webRenderer.OnLoaded = async (objs) => {
			if (!this.webRenderer || !this.webRenderer.controls) {
				return;
			}

			if (this.webRenderer.controls) {
				this.webRenderer.controls.target.x = 0;
				this.webRenderer.controls.update();

				let angle = 0;
				let origin = details.origin;

				if (origin) {
					angle = origin.includes('iQube') ? -0.25 : -0.0;
				}

				this.webRenderer.camera.position.set(0, angle, 0);
				this.webRenderer.controls.update();
			}
		};
	}

	public async SetShoeMaterial(obj: Group) {
		obj.traverse((c) => {
			if (c instanceof Mesh) {
				c.material = this.shoeShaderMaterial as ShaderMaterial;
			}
		});
	}

	sceneSetup() {}

	public async SetOutlineShoeMaterial(obj: Group) {
		obj.traverse((c) => {
			if (c instanceof Mesh) {
				c.material = this.outlineShaderMaterial as ShaderMaterial;
			}
		});
	}
}
